/**
 * Demo Connections ON / OFF for an existing tenant.
 *
 *     PATCH /api/admin/tenants/{id}/demo-connections   {"enabled": bool}
 *
 * The presentational half (`DemoConnectionsSwitchPanel`) is exported so a
 * render test can mount it against props without a network stub; the
 * fetching half is the default export and is what the tenant detail card
 * mounts.
 *
 * WHAT THIS CONTROL IS CAREFUL ABOUT
 *
 *   · LIVE is shown DISABLED with the reason, never hidden. Hiding it leaves
 *     an operator who expected the control wondering whether they
 *     misremembered; one sentence answers that.
 *   · OFF asks first, INLINE — a strip under the control, not a modal — and
 *     the strip says exactly what OFF does: it detaches the demo profile's
 *     rows and touches nothing the tenant's own users created.
 *   · The state line reads what the SERVER holds (`enabled`, `profileId`
 *     from the tenant read), never what was just clicked. A request that
 *     fails leaves the line as it was.
 *   · The note under the control says that a demo connection being present
 *     does not switch any feature on. Copy that implied otherwise is the
 *     misreading this whole area exists to close.
 */
import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { Loader2, OctagonAlert, Power, PowerOff } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { describeApiError } from '../../../utils/apiError';
import { describeEnvironment, sampleCredentialsAllowed } from './environmentPresentation';
import {
  DEMO_CONNECTIONS_DETACH_CONFIRMATION, DEMO_CONNECTIONS_FEATURE_NOTE,
} from './managedServices';

const API = process.env.REACT_APP_BACKEND_URL;

/** The permission the route requires — the same one Repair requires.
 *
 *  RESOLVED BY THE HOST PAGE, not here. `usePermission` lives beside
 *  `PermissionGate`, which pulls the tooltip primitive, and a component that
 *  imports it cannot be mounted by this repo's Jest — the card that hosts
 *  this switch was render-testable before and has to stay so. The page
 *  already gates other actions; it asks once and passes `canEdit` and the
 *  reason down. The server enforces the permission regardless. */
export const SWITCH_PERMISSION = 'tenant:update';

/**
 * The control as words and flags. Pure.
 *
 * `allowed` is the environment's rule; `canEdit` is the operator's. They are
 * kept apart because they call for different sentences: one says "never,
 * here", the other says "not you, ask an Owner".
 */
export function describeSwitch({ environment, enabled, profileId, canEdit = true, reason = null } = {}) {
  const allowed = sampleCredentialsAllowed(environment);
  const envLabel = describeEnvironment(environment).label;
  return {
    allowed,
    envLabel,
    enabled: enabled === true,
    profileId: profileId || null,
    canEdit: allowed && canEdit,
    reason: !allowed
      ? `Not available for ${envLabel} — shared demo credentials never attach to a ${envLabel} tenant.`
      : (canEdit ? null : reason),
    stateLabel: !allowed ? 'Not available' : enabled ? 'On' : 'Off',
  };
}

export function DemoConnectionsSwitchPanel({
  environment, enabled, profileId, canEdit = true, reason = null,
  busy = false, confirming = false, error = null,
  onTurnOn, onRequestOff, onConfirmOff, onCancelOff,
}) {
  const view = describeSwitch({ environment, enabled, profileId, canEdit, reason });

  return (
    <div className="rounded-xl border border-border bg-muted/20 p-4" data-testid="demo-connections-switch">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            Demo Connections
            <span
              id="demo-connections-switch-state"
              className={`ml-2 text-xs font-normal ${view.enabled && view.allowed ? 'text-success' : 'text-muted-foreground'}`}
              data-testid="demo-connections-switch-state"
            >
              {view.stateLabel}
            </span>
            {view.allowed && view.enabled && view.profileId && (
              <span className="ml-2 font-mono text-xs font-normal text-muted-foreground break-all" data-testid="demo-connections-switch-profile">
                {view.profileId}
              </span>
            )}
          </p>
          {!view.allowed ? (
            <p id="demo-connections-switch-unavailable" className="mt-1 text-xs text-muted-foreground" data-testid="demo-connections-switch-unavailable">
              {view.reason}
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              Attaches or detaches the shared {view.envLabel} demo connection profile. A reference is
              stored on the tenant; the credentials themselves never are.
            </p>
          )}
        </div>

        {/* ONE action at a time, and it is the opposite of the current state. */}
        {view.allowed && !view.enabled && (
          <Button
            size="sm"
            onClick={onTurnOn}
            disabled={!view.canEdit || busy}
            title={view.reason || undefined}
            aria-label="Turn on Demo Connections"
            aria-describedby="demo-connections-switch-state"
            data-testid="demo-connections-switch-on"
          >
            {busy
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              : <Power className="mr-2 h-4 w-4" aria-hidden="true" />}
            Turn on
          </Button>
        )}
        {view.allowed && view.enabled && !confirming && (
          <Button
            size="sm"
            variant="outline"
            onClick={onRequestOff}
            disabled={!view.canEdit || busy}
            title={view.reason || undefined}
            aria-label="Turn off Demo Connections"
            aria-describedby="demo-connections-switch-state"
            data-testid="demo-connections-switch-off"
          >
            {busy
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              : <PowerOff className="mr-2 h-4 w-4" aria-hidden="true" />}
            Turn off
          </Button>
        )}
        {!view.allowed && (
          /* Shown DISABLED rather than hidden, with the reason in the title
             and in the sentence above. */
          <Button size="sm" variant="outline" disabled title={view.reason} aria-label="Turn on Demo Connections" aria-describedby="demo-connections-switch-state demo-connections-switch-unavailable" data-testid="demo-connections-switch-disabled">
            <PowerOff className="mr-2 h-4 w-4" aria-hidden="true" />
            Turn on
          </Button>
        )}
      </div>

      {/* The OFF confirmation. Inline, under the control, in these words. */}
      {view.allowed && view.enabled && confirming && (
        <div
          className="mt-3 rounded-lg border border-warning/40 bg-warning/10 px-3 py-3"
          data-testid="demo-connections-switch-confirm"
          role="alertdialog"
          aria-label="Turn off Demo Connections"
        >
          <p className="text-sm font-medium text-foreground">Turn off Demo Connections?</p>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="demo-connections-switch-confirm-copy">
            {DEMO_CONNECTIONS_DETACH_CONFIRMATION}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="destructive" onClick={onConfirmOff} disabled={busy} data-testid="demo-connections-switch-confirm-off">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              Turn off
            </Button>
            <Button size="sm" variant="outline" onClick={onCancelOff} disabled={busy} data-testid="demo-connections-switch-cancel">
              Keep on
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 flex items-start gap-2 text-xs text-destructive" data-testid="demo-connections-switch-error" role="alert">
          <OctagonAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{error} The tenant was not changed.</span>
        </p>
      )}

      <p className="mt-3 text-xs text-muted-foreground" data-testid="demo-connections-switch-note">
        {DEMO_CONNECTIONS_FEATURE_NOTE}
      </p>
    </div>
  );
}

/** The fetching half. `canEdit` / `permissionReason` come from the host's
 *  `usePermission(SWITCH_PERMISSION)`; absent, editing is allowed here and
 *  refused by the server. */
export default function DemoConnectionsSwitch({
  tenantId, environment, enabled, profileId, onChanged, notify,
  canEdit = true, permissionReason = null,
}) {
  const reason = permissionReason;
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  const patch = useCallback(async (nextEnabled) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.patch(
        `${API}/api/admin/tenants/${tenantId}/demo-connections`,
        { enabled: nextEnabled },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
      );
      setConfirming(false);
      notify?.success(nextEnabled
        ? (data?.changed === false ? 'Demo Connections were already on' : 'Demo Connections switched on')
        : (data?.changed === false ? 'Demo Connections were already off' : 'Demo Connections switched off — demo-profile connections detached'));
      onChanged?.(data);
    } catch (err) {
      // The state line keeps reading what the server holds; the failure is
      // said beside the control rather than swallowed into a toast alone.
      setError(describeApiError(err, 'The change could not be made.'));
    } finally {
      setBusy(false);
    }
  }, [tenantId, notify, onChanged]);

  return (
    <DemoConnectionsSwitchPanel
      environment={environment}
      enabled={enabled}
      profileId={profileId}
      canEdit={canEdit}
      reason={reason}
      busy={busy}
      confirming={confirming}
      error={error}
      onTurnOn={() => patch(true)}
      onRequestOff={() => { setError(null); setConfirming(true); }}
      onConfirmOff={() => patch(false)}
      onCancelOff={() => setConfirming(false)}
    />
  );
}
